import { COLORS } from 'assets/styled';
import { IOrderOutcome, OrderStatus } from 'types/Orders.types';

// The words a status is shown with on an order's page (translation keys). An
// UNKNOWN order reads "Being checked": it may already be at the supplier.
const STATUS_LABELS: Record<string, string> = {
  [OrderStatus.SUCCESSFUL]: 'Successful',
  [OrderStatus.FAILED]: 'Failed',
  [OrderStatus.PENDING]: 'Pending',
  [OrderStatus.SENDING]: 'Sending',
  [OrderStatus.UNKNOWN]: 'Being checked',
};

export const formatStatus = (status: string | undefined): string => {
  if (!status) return '';
  return (
    STATUS_LABELS[status] ??
    status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  );
};

// A status this version does not know is shown in the text colour, never green.
export const getOrderStatusColor = (status: string | undefined): string => {
  switch (status) {
    case OrderStatus.SUCCESSFUL:
      return COLORS.GREEN;
    case OrderStatus.FAILED:
      return COLORS.RED;
    case OrderStatus.PENDING:
      return COLORS.YELLOW;
    case OrderStatus.SENDING:
      return COLORS.BLUE;
    case OrderStatus.UNKNOWN:
      return COLORS.SALMON;
    default:
      return COLORS.BLACK;
  }
};

interface IOrderState extends IOrderOutcome {
  status?: string;
  connector_id?: number | null;
}

// A FAILED order Logyx core will not send again (resend_allowed false: a
// supplier number, a send that was created or unresolved, not safe to resend)
// may already be at the supplier: someone has to check there. Only one that went
// to a connector: one without (null, or a core that does not send it) is a
// manual order the supplier set FAILED, and stays plain FAILED. A core older
// than BP-1219 sends no resend_allowed, and then it stays plain FAILED too.
export const isOrderToCheckWithSupplier = (
  order: IOrderState | undefined
): boolean =>
  order?.status === OrderStatus.FAILED &&
  order.resend_allowed === false &&
  order.connector_id != null;

// An order's label on its page: "Check with supplier" for such an order.
export const formatOrderStatus = (order: IOrderState | undefined): string =>
  isOrderToCheckWithSupplier(order)
    ? 'Check with supplier'
    : formatStatus(order?.status);

// An order's colour on its page and in its note: such an order "may already be
// at the supplier", as an UNKNOWN one, so it takes UNKNOWN's colour.
export const getOrderColor = (order: IOrderState | undefined): string =>
  isOrderToCheckWithSupplier(order)
    ? COLORS.SALMON
    : getOrderStatusColor(order?.status);

// An order's status in the orders lists, which translate it: the status's own
// name (UNKNOWN reads "BEING CHECKED"), or "CHECK WITH SUPPLIER".
export const getOrderListStatus = (order: IOrderState): string =>
  isOrderToCheckWithSupplier(order)
    ? 'CHECK WITH SUPPLIER'
    : order.status ?? '';

// SENDING and UNKNOWN are Logyx core's to settle: nobody changes such an order
// by hand, and its page keeps asking how it stands.
export const isOrderBeingSettled = (status: string | undefined): boolean =>
  status === OrderStatus.SENDING || status === OrderStatus.UNKNOWN;

// Retry follows Logyx core's resend_allowed. A core older than BP-1219 sends no
// such field, and then the rule from before applies, so Forms does not have to
// be released at the very same moment as core. An order without connector_id
// (null, or missing) has no connector, as in isOrderToCheckWithSupplier.
export const canRetryOrder = (order: IOrderState | undefined): boolean => {
  if (!order) return false;
  if (typeof order.resend_allowed === 'boolean') return order.resend_allowed;
  return order.status === OrderStatus.FAILED && order.connector_id != null;
};

// How often an order's page asks for it again while Logyx core settles it: the
// cadence of erp-fe's purchase order page. A SENDING order settles within
// seconds or, when its send was cut, through core's outcome job: usually within
// about 8 minutes, up to an hour when the connector cannot be asked (or still
// reports the send in progress). An UNKNOWN one can take days, until someone
// checks it.
export const getOrderPollInterval = (
  status: string | undefined,
  sendingForMs: number
): number | false => {
  if (status === OrderStatus.SENDING) {
    if (sendingForMs < 60 * 1000) return 5 * 1000;
    if (sendingForMs < 10 * 60 * 1000) return 15 * 1000;
    return 30 * 1000;
  }
  if (status === OrderStatus.UNKNOWN) return 60 * 1000;
  return false;
};
